import type { AdminTrainingJob } from "@repo/marketplace-admin"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import type { ColDef, ICellRendererParams } from "ag-grid-community"
import { Play, RefreshCw } from "lucide-react"
import * as React from "react"
import { toast } from "sonner"
import { PageHeader, Toolbar } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { Textarea } from "@/components/ui/textarea"
import { ScannerGrid } from "@/features/card-scanner/ScannerGrid"
import {
	formatDuration,
	HeartbeatCell,
	Instant,
	JOB_STATUSES,
	MODEL_TYPE_LABELS,
	MODEL_TYPES,
	type ModelType,
	QueryError,
	StatusBadge,
	TERMINAL_JOB_STATUSES,
	useScannerGate,
} from "@/features/card-scanner/shared"
import { useMarketplaceOrpc } from "@/integrations/orpc/marketplace-react"

export const Route = createFileRoute("/card-scanner/")({
	component: TrainingJobsPage,
	validateSearch: (search: Record<string, unknown>): { train?: boolean } =>
		search.train === "1" || search.train === true ? { train: true } : {},
})

const ALL = "__all__"

function TrainingJobsPage() {
	const allowed = useScannerGate()
	const navigate = useNavigate()
	const { orpc, isAuthReady } = useMarketplaceOrpc()
	const queryClient = useQueryClient()

	const [typeFilter, setTypeFilter] = React.useState<string>(ALL)
	const [statusFilter, setStatusFilter] = React.useState<string>(ALL)
	const [trainDialogOpen, setTrainDialogOpen] = React.useState(false)
	const { train } = Route.useSearch()

	React.useEffect(() => {
		if (!train) return
		setTrainDialogOpen(true)
		navigate({ to: "/card-scanner", search: {}, replace: true })
	}, [train, navigate])

	const input = {
		type: typeFilter === ALL ? undefined : (typeFilter as ModelType),
		status:
			statusFilter === ALL
				? undefined
				: (statusFilter as (typeof JOB_STATUSES)[number]),
	}
	const { data, isLoading, isError, error, refetch, isRefetching } = useQuery({
		...orpc.cardScanner.listTrainingJobs.queryOptions({ input }),
		enabled: allowed && isAuthReady,
		refetchInterval: (query) =>
			query.state.data?.jobs.some(
				(job) => !TERMINAL_JOB_STATUSES.includes(job.status),
			)
				? 10_000
				: false,
	})

	const columnDefs = React.useMemo<ColDef<AdminTrainingJob>[]>(
		() => [
			{
				headerName: "Type",
				field: "type",
				minWidth: 170,
				valueFormatter: (p) =>
					MODEL_TYPE_LABELS[p.value as ModelType] ?? p.value,
			},
			{
				headerName: "Status",
				field: "status",
				minWidth: 150,
				cellRenderer: (p: ICellRendererParams<AdminTrainingJob>) =>
					p.data ? <StatusBadge status={p.data.status} /> : null,
			},
			{
				headerName: "Started",
				field: "startedAt",
				sort: "desc",
				minWidth: 140,
				cellRenderer: (p: ICellRendererParams<AdminTrainingJob>) =>
					p.data ? <Instant iso={p.data.startedAt} /> : null,
			},
			{
				headerName: "Duration",
				colId: "duration",
				minWidth: 100,
				sortable: false,
				type: "rightAligned",
				cellClass: "tabular-nums",
				valueGetter: (p) =>
					p.data ? formatDuration(p.data.startedAt, p.data.finishedAt) : "",
			},
			{
				headerName: "Heartbeat",
				colId: "heartbeat",
				minWidth: 130,
				sortable: false,
				cellRenderer: (p: ICellRendererParams<AdminTrainingJob>) =>
					p.data ? (
						<HeartbeatCell
							iso={p.data.lastHeartbeatAt}
							active={!TERMINAL_JOB_STATUSES.includes(p.data.status)}
						/>
					) : null,
			},
			{
				headerName: "Triggered by",
				field: "triggeredBy",
				minWidth: 140,
				valueFormatter: (p) => p.value ?? "—",
			},
			{
				headerName: "Instance",
				field: "ec2InstanceId",
				minWidth: 150,
				sortable: false,
				cellClass: "font-mono",
			},
			{
				headerName: "Outcome",
				colId: "outcome",
				minWidth: 150,
				sortable: false,
				cellRenderer: (p: ICellRendererParams<AdminTrainingJob>) => {
					if (!p.data) return null
					if (p.data.status === "done") {
						return (
							<span className="text-muted-foreground">model registered</span>
						)
					}
					if (p.data.status === "failed") {
						return (
							<span className="max-w-40 truncate text-bad">
								{p.data.failureReason ?? "failed"}
							</span>
						)
					}
					return <span className="text-meta">in flight</span>
				},
			},
		],
		[],
	)

	if (!allowed) return null

	return (
		<div className="flex flex-col gap-6 p-6">
			<PageHeader
				title="Training jobs"
				count={data?.jobs.length}
				description="GPU training runs registered by scanner instances"
				actions={
					<>
						<Button
							variant="outline"
							size="icon"
							aria-label="Refresh training jobs"
							onClick={() => refetch()}
							disabled={isRefetching}
						>
							<RefreshCw className="h-4 w-4" />
						</Button>
						<Button onClick={() => setTrainDialogOpen(true)}>
							<Play className="h-4 w-4" />
							Train model
						</Button>
					</>
				}
			/>

			<Toolbar>
				<Select value={typeFilter} onValueChange={setTypeFilter}>
					<SelectTrigger className="w-56">
						<SelectValue placeholder="All types" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value={ALL}>All types</SelectItem>
						{MODEL_TYPES.map((type) => (
							<SelectItem key={type} value={type}>
								{MODEL_TYPE_LABELS[type]}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				<Select value={statusFilter} onValueChange={setStatusFilter}>
					<SelectTrigger className="w-48">
						<SelectValue placeholder="All statuses" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value={ALL}>All statuses</SelectItem>
						{JOB_STATUSES.map((status) => (
							<SelectItem key={status} value={status}>
								{status.replaceAll("_", " ")}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</Toolbar>

			{isLoading ? (
				<Skeleton className="h-64 w-full" />
			) : isError ? (
				<QueryError what="training jobs" error={error} />
			) : data?.jobs.length === 0 ? (
				<p className="text-muted-foreground">
					No training jobs. A dispatched job appears here once its GPU instance
					registers, about 3 minutes later.
				</p>
			) : (
				<ScannerGrid<AdminTrainingJob>
					columnDefs={columnDefs}
					rowData={data?.jobs}
					getRowId={(row) => row.id}
					onRowClicked={(row) =>
						navigate({
							to: "/card-scanner/jobs/$jobId",
							params: { jobId: row.id },
						})
					}
					gridOptions={{ rowClass: "cursor-pointer" }}
				/>
			)}

			<TrainModelDialog
				open={trainDialogOpen}
				onOpenChange={setTrainDialogOpen}
				onDispatched={() =>
					queryClient.invalidateQueries({
						queryKey: orpc.cardScanner.listTrainingJobs.key(),
					})
				}
			/>
		</div>
	)
}

function TrainModelDialog({
	open,
	onOpenChange,
	onDispatched,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	onDispatched: () => void
}) {
	const { client } = useMarketplaceOrpc()
	const [type, setType] = React.useState<ModelType>("classifier")
	const [paramsText, setParamsText] = React.useState("")
	const [force, setForce] = React.useState(false)

	const mutation = useMutation({
		mutationFn: async () => {
			let params: Record<string, unknown> | undefined
			if (paramsText.trim()) {
				params = JSON.parse(paramsText) as Record<string, unknown>
			}
			return client.cardScanner.triggerTraining({ type, params, force })
		},
		onSuccess: ({ triggerId }) => {
			toast.success(`Training dispatched (trigger ${triggerId.slice(0, 8)}…)`)
			onOpenChange(false)
			setParamsText("")
			setForce(false)
			onDispatched()
		},
		onError: (error) => {
			toast.error(error.message)
		},
	})

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Train model</DialogTitle>
					<DialogDescription>
						Launches a GPU training instance via the train-model workflow. The
						job appears here once the instance registers, about 2 minutes later.
					</DialogDescription>
				</DialogHeader>
				<div className="space-y-4">
					<div className="space-y-2">
						<Label>Model type</Label>
						<Select
							value={type}
							onValueChange={(value) => setType(value as ModelType)}
						>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{MODEL_TYPES.map((modelType) => (
									<SelectItem key={modelType} value={modelType}>
										{MODEL_TYPE_LABELS[modelType]}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div className="space-y-2">
						<Label>Params (JSON, optional)</Label>
						<Textarea
							value={paramsText}
							onChange={(event) => setParamsText(event.target.value)}
							placeholder='{"epochs": 200}'
							rows={3}
						/>
					</div>
					<div className="flex items-center gap-2">
						<Checkbox
							id="force-train"
							checked={force}
							onCheckedChange={(checked) => setForce(checked === true)}
						/>
						<Label htmlFor="force-train">
							Force (skip the already-running check)
						</Label>
					</div>
				</div>
				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Cancel
					</Button>
					<Button
						onClick={() => mutation.mutate()}
						disabled={mutation.isPending}
					>
						Dispatch
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
